/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { Button } from 'ui/atoms/Button/Button';
import { Faq } from 'ui/atoms/Faq/Faq';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { EditorialStepsSection } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection';
import { Section } from 'ui/molecules/Section/Section';
import { OurCustomersV2 } from 'ui/molecules/OurCustomers/OurCustomersV2';
import { ScreeningChainCard } from 'ui/molecules/ScreeningChainCard/ScreeningChainCard';
import { ComparisonMatrix } from 'ui/molecules/ComparisonMatrix/ComparisonMatrix';
import { SanctionsCoverage } from 'ui/molecules/SanctionsCoverage/SanctionsCoverage';
import { PepsMonitoringFeed } from 'ui/molecules/PepsMonitoringFeed/PepsMonitoringFeed';
import { CtaSection } from 'ui/organisms/CtaSection/CtaSection';

import styles from './PepsSanctionsPage.module.scss';

import { RegisPrompt } from './RegisPrompt';
import {
  getComparisonRows,
  getCoverageCards,
  getFaqItems,
  getFeatureSteps,
  getHeroStats,
  getMonitoringAlerts,
  getMonitoringBullets,
  getRegisBullets,
  getScreening,
  MONITORING_WATCHING,
} from './PepsSanctionsPage.data';

export const PepsSanctionsPage = () => {
  const { t } = useInt();
  const { secondaryNavigation } = useContext(ContentContext);

  const freeTrialSlug = secondaryNavigation?.free_trial?.slug;

  const heroStats = getHeroStats(t);
  const screening = getScreening(t);
  const featureSteps = getFeatureSteps(t);
  const comparisonRows = getComparisonRows(t);
  const coverageCards = getCoverageCards(t);
  const monitoringBullets = getMonitoringBullets(t);
  const monitoringAlerts = getMonitoringAlerts(t);
  const regisBullets = getRegisBullets(t);

  const bullets = (items: string[]) => (
    <ul className={styles.bullets}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );

  return (
    <div className={styles.wrapper}>
      {/* ── Hero ── */}
      <header className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <TitleBlock
              size="page"
              align="left"
              title={t('Sanctions & PEP screening that finds the owner three layers up.')}
              description={
                <>
                  {t('Name matching stops at the counterparty. We resolve every entity to its official registry record and screen the')}{' '}
                  <strong>{t('full ownership chain — directors, shareholders and UBOs')}</strong>{' '}
                  {t('— against global sanctions and PEP data.')}
                </>
              }
            />

            {freeTrialSlug && (
              <div className={styles.heroCta}>
                <Button type="blue" size="xxl" route={freeTrialSlug}>
                  {t('Free Trial')}
                </Button>
              </div>
            )}

            <div className={styles.heroStats}>
              {heroStats.map((stat) => (
                <div key={stat.label} className={styles.heroStat}>
                  <b>{stat.value}</b>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          <ScreeningChainCard className={styles.heroWidget} {...screening} />
        </div>
      </header>

      {/* ── Logo strip ── */}
      <OurCustomersV2 hasBackground={false} />

      {/* ── Features (numbered editorial) ── */}
      <EditorialStepsSection
        background="blue"
        title={t("Screening a name is guesswork. Screening a record isn't.")}
        description={t(
          'Legacy tools match strings and leave your analysts to clean up. We match legal entities — built on primary, government-sourced data from 400+ government registries.',
        )}
        steps={featureSteps}
      />

      {/* ── Comparison ── */}
      <Section title={t('Name-based screening vs. registry-resolved screening')} className={styles.cmpSection}>
        <ComparisonMatrix
          usLabel={<span className={styles.brandLabel}>Global Database</span>}
          themLabel={t('Name-based tools')}
          rows={comparisonRows}
        />
      </Section>

      {/* ── Coverage ── */}
      <Section title={t('Every list that matters. Every person who does.')}>
        <SanctionsCoverage cards={coverageCards} />
      </Section>

      {/* ── Monitoring ── */}
      <Section
        layout="split"
        title={t('Add your portfolio once. We watch it 24/7.')}
        description={t(
          'Every counterparty, director and UBO — re-screened around the clock. When something changes, the alert lands with the evidence attached.',
        )}
        actions={bullets(monitoringBullets)}
      >
        <PepsMonitoringFeed
          watching={MONITORING_WATCHING}
          alerts={monitoringAlerts}
          title={t('Monitoring alerts')}
          watchingLabel={t('entities · directors & UBOs included')}
          watchingPrefix={t('Watching')}
          liveLabel={t('LIVE')}
        />
      </Section>

      {/* ── Regis AI ── */}
      <Section
        theme="dark"
        layout="split"
        background="#0e1526"
        extra={t('✦ Regis AI')}
        title={t("Ask the question the way you'd ask an analyst.")}
        description={t(
          'Regis runs the full screen — entity, directors, ownership chain — from a single question, and answers with registry citations, not confidence scores.',
        )}
        actions={
          <div className={styles.regisLeft}>
            {bullets(regisBullets)}
            <Button type="blue" size="xl" href="/regis">
              {t('Try Regis free')}
            </Button>
          </div>
        }
        className={styles.regisSection}
      >
        <RegisPrompt />
      </Section>

      {/* ── CTA ── */}
      <CtaSection
        spotlight
        title={t('Screen the structure, not the string.')}
        description={t(
          "Bring your hardest counterparties to the call. We'll run them live — ownership chains, UBOs and all.",
        )}
        actions={
          secondaryNavigation?.book_demo?.slug && (
            <Button type="blue" size="xxl" route={secondaryNavigation.book_demo.slug}>
              {t('Book a demo')}
            </Button>
          )
        }
      />

      {/* ── FAQ ── */}
      <Section>
        <Faq className={styles.faq} title={t('Before your risk committee asks, here are the answers.')} data={getFaqItems(t)} />
      </Section>
    </div>
  );
};
